import React, { useContext, useCallback, useState, useEffect } from "react";
import { ResponsiveSize, ValidationState } from "../Types";
import AsyncSelect from "react-select/async-creatable";
import { Address, AddressModal, CountryStates } from "./AddressModal";
import { reactSelectStyles } from './PickerStyles';
import { LabelContext } from "../labels/LabelContext";
import { GroupContext } from "../layout/GroupContext";
import { debounce } from './Debounce'
import classNames from "classnames";
import styles from "./PickerStyles.scss";
import { loadGoogle } from "./GoogleLoader";

export interface AddressPickerProps {
    /**
     * Control id
     */
    id?: string;

    /**
     * Control value
     */
    value: Address | null;

    /**
     * Control size
     */
    size?: ResponsiveSize;

    /**
     * Control state
     */
    state?: ValidationState;

    /**
     * Trigger OnChnage
     */
    onChange: (v: Address | null) => void;

    /**
     * Trigger disabled
     */
    disabled?: boolean;

    /**
     * Custom class name
     */
    className?: string;
    /**
     * Placeholder
     */
    placeholder?: CountryStates
    /**
     * Country states dictionary
     */
    countryStates?: CountryStates
}

interface AddressOption {
    label: string
    place: google.maps.places.PlaceResult | null
}

export interface AddressComponent {
    long_name: string;
    short_name: string;
    types: string[];
}

export interface AddressDetails {
    address_components: AddressComponent[];
    formatted_address: string;
    types: string[];
}

export interface AddressResponse {
    results: AddressDetails[];
    status: string;
}

const addressLongNameUse = {
    premise: false,
    street_number: false,
    route: true,
    locality: true,
    administrative_area_level_1: false,
    country: true,
    postal_code: true,
    subpremise: true,
};

type AddressParts = keyof typeof addressLongNameUse;
type AddressObject<T = typeof addressLongNameUse> = {
    [k in keyof T]+?: string | null
}

const getPlaceAddress = async (placeId: string): Promise<Address | null> => {
    return new Promise<Address | null>((resolve, reject) => {
        const request: google.maps.GeocoderRequest = {
            placeId: placeId,
        };

        const geocoder = new google.maps.Geocoder();
        geocoder.geocode(request, (
            response: google.maps.GeocoderResult[] | null,
            status: google.maps.GeocoderStatus
        ) => {
            if (status === google.maps.GeocoderStatus.OK) {
                if (response) {
                    const data = response as AddressDetails[];
                    if (!data || data.length == 0) {
                        reject({ status });
                    }
                    const details = data[0];
                    var obj: AddressObject = {};
                    for (let p in addressLongNameUse) {
                        for (const component of details.address_components) {
                            if (component.types.indexOf(p) >= 0) {
                                obj[p as AddressParts] = addressLongNameUse[p as AddressParts] ? component.long_name : component.short_name;
                            }
                        }
                    }
                    let res = {
                        addressLine1: [obj.premise, obj.subpremise, obj.street_number, obj.route].filter(r => r && r.length > 0).join(' '),
                        suburb: obj.locality || '',
                        state: obj.administrative_area_level_1 ?? '',
                        postCode: obj.postal_code ?? '',
                        country: obj.country ?? ''
                    };
                    resolve(res);
                }
                else {
                    resolve(null)
                }
            }
            else {
                reject({ status });
            }
        });
    });
};

const formatAddress = (value: Address) => {
    const temp = [value.suburb, value.state, value.postCode].filter(r => r && r.length > 0).join(' ');
    return [value.addressLine1, temp, value.country].filter(r => r && r.length > 0).join(', ');
}

export const AddressPicker: React.FC<AddressPickerProps> = ({ size = 'block', ...props }) => {
    const [open, setOpen] = useState(false);
    const [value, setValue] = useState<AddressOption | null>(() => {
        if (props.value) {
            return {
                label: formatAddress(props.value),
                place: null
            }
        }
        else {
            return null;
        }
    });

    useEffect(() => {
        if (props.value) {
            let label = formatAddress(props.value);
            if (!value || value.label != label) {
                setValue({
                    label: label,
                    place: null
                });
            }
        }
        else if (value) {
            setValue(null);
        }
    }, [props.value])

    const onSelect = (v: AddressOption | null) => {
        setValue(v);
        if (v && v.place && v.place.place_id) {
            getPlaceAddress(v.place.place_id).then(a => {
                props.onChange && props.onChange(a);
            });
        }
        else {
            props.onChange && props.onChange(null);
        }
    }

    const onSet = (v: Address) => {
        setValue({
            label: formatAddress(v),
            place: null
        });
        props.onChange && props.onChange(v);
    }

    const labelCtx = useContext(LabelContext);
    const groupCtx = useContext(GroupContext);
    const state = labelCtx.state || props.state;

    const search = async (s: string) => {
        const places = await loadGoogle()

        return new Promise<AddressOption[]>((resolve, reject) => {
            const request: google.maps.places.FindPlaceFromQueryRequest = {
                query: s,
                fields: ["name", "geometry", "formatted_address"],
            };
            places.textSearch(
                request,
                (
                    response: google.maps.places.PlaceResult[] | null,
                    status: google.maps.places.PlacesServiceStatus
                ) => {
                    if (status === google.maps.places.PlacesServiceStatus.OK) {
                        if (response) {
                            let res = response.map((place: google.maps.places.PlaceResult) => ({
                                label: place.formatted_address ?? "",
                                place: place,
                            }));
                            resolve(res);
                        }
                        else {
                            resolve([])
                        }
                    }
                    else {
                        reject({ status });
                    }
                }
            );
        });
    }

    const debouncedSearch = useCallback(debounce(search, 1000), []);

    return <>
        <div className={classNames(styles["au-picker"], {
            [styles[`au-picker--width-${size}`]]: size && size != 'block',
            [styles[`au-picker--block`]]: size == 'block'
        }, groupCtx.className, props.className)}>
            <AsyncSelect<AddressOption>
                inputId={props.id || labelCtx.id}
                styles={reactSelectStyles(state)}
                className={classNames(groupCtx.className, props.className)}
                placeholder={props.placeholder}
                loadOptions={debouncedSearch}
                value={value}
                onChange={onSelect}
                getOptionValue={r => r.label}
                isClearable
                isDisabled={props.disabled}
                cacheOptions={false}
                formatCreateLabel={() => `Can't find address? Enter it manually...`}
                onCreateOption={() => setOpen(true)}
            />
        </div>
        <AddressModal
            id={`${props.id || labelCtx.id}-modal`}
            open={open}
            onClose={() => setOpen(false)}
            value={{ addressLine1: '', country: '', postCode: '', state: '', suburb: '' }}
            onChange={onSet}
            countryStates={props.countryStates}
        />
    </>
}